import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export const QLQuality = () => {
  const [data, setData] = useState(null);
  const [issues, setIssues] = useState(null);
  const [filter, setFilter] = useState({});

  useEffect(() => {
    api.get("/quantlab/quality").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const drill = (f) => {
    setFilter(f);
    const p = new URLSearchParams();
    if (f.batch_id) p.set("batch_id", f.batch_id);
    if (f.issue_type) p.set("issue_type", f.issue_type);
    api.get(`/quantlab/quality/issues?${p}`).then((r) => setIssues(r.data.items || [])).catch(() => {});
  };

  if (!data) return <div className="text-zinc-600 font-mono text-xs py-10 text-center">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="ql-quality">
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(data.totals || {}).map(([k, v]) => (
          <div key={k} className="border border-zinc-800 bg-[#0a0a0a] p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{k.replace("_", " ")}</div>
            <div className="font-mono text-2xl font-bold mt-1" data-testid={`ql-total-${k}`}>{v.toLocaleString("en-IN")}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="border border-zinc-800 bg-[#0a0a0a]">
          <div className="px-4 py-3 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Issues by type (click to inspect)</div>
          <table className="w-full text-xs font-mono">
            <tbody>
              {(data.issues_by_type || []).length === 0 && <tr><td className="px-4 py-6 text-center text-zinc-600">No data issues recorded.</td></tr>}
              {(data.issues_by_type || []).map((i, idx) => (
                <tr key={idx} className="border-b border-zinc-900/50 hover:bg-zinc-900/40 cursor-pointer"
                    onClick={() => drill({ issue_type: i.issue_type })} data-testid={`ql-issue-type-${i.issue_type}`}>
                  <td className="px-4 py-2">{i.issue_type}</td>
                  <td className={`px-4 py-2 ${i.severity === "error" ? "text-red-400" : "text-[#F59E0B]"}`}>{i.severity}</td>
                  <td className="px-4 py-2 font-bold">{i.count.toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border border-zinc-800 bg-[#0a0a0a]">
          <div className="px-4 py-3 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Underlying coverage (5m grid, 75 candles/session)</div>
          <table className="w-full text-xs font-mono">
            <thead><tr className="text-zinc-500 text-[10px] uppercase border-b border-zinc-900">
              {["Symbol", "Days", "Candles", "Expected", "Missing est."].map((h) => <th key={h} className="px-4 py-2 text-left">{h}</th>)}
            </tr></thead>
            <tbody>
              {(data.coverage || []).length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-zinc-600">No underlying data yet.</td></tr>}
              {(data.coverage || []).map((c) => (
                <tr key={c.symbol} className="border-b border-zinc-900/50">
                  <td className="px-4 py-2 font-bold">{c.symbol}</td>
                  <td className="px-4 py-2">{c.days}</td>
                  <td className="px-4 py-2">{c.candles.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2 text-zinc-500">{c.expected.toLocaleString("en-IN")}</td>
                  <td className={`px-4 py-2 ${c.missing_estimate > 0 ? "text-[#F59E0B]" : "text-[#22C55E]"}`}>{c.missing_estimate.toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {issues && (
        <div className="border border-zinc-800 bg-[#0a0a0a]" data-testid="ql-issue-detail">
          <div className="px-4 py-3 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            Issue detail {filter.issue_type && `· ${filter.issue_type}`} {filter.batch_id && `· ${filter.batch_id}`} ({issues.length})
          </div>
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-xs font-mono">
              <thead><tr className="text-zinc-500 text-[10px] uppercase border-b border-zinc-900">
                {["Type", "Severity", "Symbol", "Row", "Timestamp", "Contract", "Detail"].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}
              </tr></thead>
              <tbody>
                {issues.map((i, idx) => (
                  <tr key={idx} className="border-b border-zinc-900/50">
                    <td className="px-3 py-1.5">{i.issue_type}</td>
                    <td className={`px-3 py-1.5 ${i.severity === "error" ? "text-red-400" : "text-[#F59E0B]"}`}>{i.severity}</td>
                    <td className="px-3 py-1.5">{i.symbol || "—"}</td>
                    <td className="px-3 py-1.5">{i.row ?? "—"}</td>
                    <td className="px-3 py-1.5 text-zinc-500">{i.ts || "—"}</td>
                    <td className="px-3 py-1.5 text-zinc-500">{i.contract_id || "—"}</td>
                    <td className="px-3 py-1.5 text-zinc-400">{i.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default QLQuality;
